import { Heading, MenuItem, Stack } from "@biom3/react";
import Image from "next/image";
import { type Connector, useAccount, useDisconnect } from "wagmi";

import { ActionModal } from "@/components/modal/ActionModal";
import { walletIconProps } from "@/components/wallets/utils/walletIconParams";
import { CONNECTOR_NAME_MAP } from "@/types";

export const WalletConnectorsModal = ({
  isOpen,
  onClose,
  onConnectorClick,
  validConnectors,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConnectorClick: (connector: Connector) => void;
  validConnectors: Connector[];
}) => {
  const { disconnectAsync } = useDisconnect();
  const { isConnected, connector: activeConnector } = useAccount();

  const handleConnectorClick = async (connector: Connector) => {
    // disconnect if it's already connected
    if (isConnected) {
      try {
        await disconnectAsync({ connector: activeConnector });
      } catch (error) {
        console.error("Error during disconnectAsync", error);
      }
    }
    onConnectorClick(connector);
  };

  return (
    <ActionModal isModalOpen={isOpen} onClose={onClose}>
      <Heading
        sx={{ pl: "base.spacing.x3", pr: "base.spacing.x3" }}
        size="medium"
        weight="bold"
      >
        Choose your wallet
      </Heading>
      <Stack gap="base.spacing.x2">
        {validConnectors.map((connector) => {
          const { alt, sx, imagePath, testId } = walletIconProps(connector.id);
          return (
            <MenuItem
              key={connector.id}
              emphasized
              onClick={() => handleConnectorClick(connector)}
              size="small"
            >
              <MenuItem.FramedImage
                {...{ alt, testId, sx }}
                use={
                  <Image
                    alt={alt}
                    src={connector.icon?.trim() ?? imagePath}
                    width={48}
                    height={48}
                  />
                }
              />
              <MenuItem.Label>
                {connector.id === "walletConnect"
                  ? CONNECTOR_NAME_MAP[connector.id]
                  : connector.name}
              </MenuItem.Label>
              <MenuItem.IntentIcon icon="ChevronForward" />
            </MenuItem>
          );
        })}
      </Stack>
    </ActionModal>
  );
};
